import { SquareArrowOutUpRight, Layers, RotateCcw } from "lucide-react";
import { useToastStore } from "../../../stores/toastStore";
import { useVisualizerStore } from "./store";
import { JSONUploader } from "./JSONUploader";
import { ComponentTree } from "./ComponentTree";
import { ComponentGraph } from "./ComponentGraph";
import { ComponentDetailsPanel } from "./ComponentDetailsPanel";

export function ComponentDefinitionVisualizer() {
  const { raw, fileName, reset, components, capabilities, resources } =
    useVisualizerStore();

  const handleExport = () => {
    if (!raw) return;
    const blob = new Blob([JSON.stringify(raw, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "component-definition.json";
    a.click();
    URL.revokeObjectURL(url);
    useToastStore.getState().showSuccess("Success", "Exported successfully");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-lg bg-indigo-600 dark:bg-indigo-600 flex items-center justify-center">
            <Layers className="size-4 text-white" />
          </div>
          <div>
            <h1 className="text-[24px] font-bold text-foreground tracking-tight">
              Component Definition Visualizer
            </h1>
            {fileName && <p className="text-[10px] d font-mono">{fileName}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {raw && (
            <>
              {/* Stats badges */}
              <div className="hidden md:flex items-center gap-2 mr-2">
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-muted d">
                  {components.length} Components
                </span>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-muted d">
                  {capabilities.length} Capabilities
                </span>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-muted d">
                  {resources.length} Resources
                </span>
              </div>
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium d hover:text-foreground hover:bg-accent transition-colors"
              >
                <SquareArrowOutUpRight className="size-3" /> Export
              </button>
              <button
                onClick={reset}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium d hover:text-foreground hover:bg-accent transition-colors"
              >
                <RotateCcw className="size-3" /> Reset
              </button>
            </>
          )}
          <JSONUploader />
        </div>
      </div>

      {/* ── Main Layout: 3 panels ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Tree Navigation */}
        <div className="w-72 border-r border-border bg-card flex-shrink-0 overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-border">
            <span className="text-[10px] font-bold d tracking-widest">
              Navigation
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ComponentTree />
          </div>
        </div>

        {/* Center Panel - Graph Visualization */}
        <div className="flex-1 bg-background overflow-hidden">
          <ComponentGraph />
        </div>

        {/* Right Panel - Details */}
        <div className="w-80 border-l border-border bg-card flex-shrink-0 overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-border">
            <span className="text-[10px] font-bold d tracking-widest">
              Details
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ComponentDetailsPanel />
          </div>
        </div>
      </div>
    </div>
  );
}
